import { PropsWithChildren, createContext, useCallback, useContext, ChangeEvent, useEffect, useMemo, useReducer } from "react"
import requests from "./api/client"
import { useInitReq } from "./InitReq"
import { useSettings } from "./Settings"
import dayjs, { Dayjs } from "dayjs"
import { PurchaseSummaryParams } from "./types/otp"

const MOCK = process.env.REACT_APP_MOCK === "true"

export interface CompaniesTelProps {
  id?: number
  descricao?: string
}

export interface CompaniesTelData {
  retorno: boolean
  data: CompaniesTelProps[]
}

/***** FIELDS DO FORMULÁRIO *****/
export interface CitiesListProps {
  id: number
  cidade: string
  uf: string
}

export interface FormFields {
  // FIELDS 2
  name: string
  cpfCnpj: string
  date: string | Dayjs | null
  email: string
  ddd: string
  telephone: string
  // FIELDS 3
  cep: string,
  city?: string,
  cityId?: number,
  state?: string,
  uf?: string,
  street: string,
  neighborhood: string,
  number: string,
  complement: string,
  reference: string,
  externalId?: string
  // PORTABILITY FIELDS
  companiesTel: CompaniesTelProps[] | []
  telToTransfer: string
  telCompany?: number
  companyLabel?: string
  // CARD FORM
  cardName: string
  cardNumber: string
  cardIssueDate: string | Dayjs | null
  cardSecurityCode: string
  cardInstallments: string
  // REQ PARAMS
  cartId: string
  cartNavigate: boolean
  citiesList: CitiesListProps[]
  cityNameUpdate: string
  cityIdUpdate: string
  cardMethodDisabled: boolean
  keepCardDisabled: boolean
  keepCardChecked: boolean
  paymentMethod: 'PIX' | 'CARTAO' | 'BOLETO' | 'LINK' | 'UNIXCARD' | 'DISABLED'
  paymentOption: 'oneTime' | 'installment'
  clientId: number
  installmentLink: string
  voucherClub: string
  isVoucherValid: boolean
  voucherName: string
  // OTP
  hash: string
  loadingSummary: boolean
  errorSummary: string | null
  /** SDK 3DS não apareceu no prazo: pagamento com cartão indisponível. */
  sdk3DSIndisponivel: boolean
  purchaseSummaryWithOtp: PurchaseSummaryParams | null
  /** Erros de validação vindos do 422 da API, já mapeados para campos do form. */
  fieldErrors: Record<string, string[]> | null
}

interface FormState {
  form1: boolean
  form2: boolean
  form3: boolean
  cardForm: boolean
  cardFormInvalid: boolean
  showError: boolean
  step1Error: boolean
  step1Invalid: boolean
  step2Error: boolean
  disabledBtn: boolean
}

const defaultForm: FormFields = {
  // FIELDS 2
  name: '',
  cpfCnpj: '',
  date: null, // dayjs(date).format('YYYY-MM-DD')
  email: '',
  ddd: '',
  telephone: '',
  // FIELDS 3
  cep: '',
  city: undefined,
  cityId: undefined,
  state: undefined,
  uf: undefined,
  street: '',
  neighborhood: '',
  number: '',
  complement: '',
  reference: '',
  externalId: '',
  // PORTABILITY FIELDS
  companiesTel: [],
  telToTransfer: '',
  telCompany: undefined,
  companyLabel: undefined,
  // CARD FORM
  cardName: '',
  cardNumber: '',
  cardIssueDate: null,
  cardSecurityCode: '',
  cardInstallments: '',
  // REQ PARAMS
  cartId: '',
  cartNavigate: false,
  citiesList: [],
  cityNameUpdate: '',
  cityIdUpdate: '',
  cardMethodDisabled: false,
  keepCardDisabled: false,
  keepCardChecked: true,
  paymentMethod: 'CARTAO',
  paymentOption: 'oneTime',
  clientId: 0,
  installmentLink: '',
  voucherClub: '',
  isVoucherValid: false,
  voucherName: '',
  // OTP
  hash: '',
  loadingSummary: true,
  errorSummary: null,
  sdk3DSIndisponivel: false,
  purchaseSummaryWithOtp: null,
  fieldErrors: null
}

const mockForm: FormFields = {
  ...defaultForm,
  name: 'Matheus De Mucio Shirasawa',
  cpfCnpj: '523.098.178-48',
  date: dayjs('2003-09-30'),
  email: 'mathshiro@gmail.com',
  ddd: '(11)',
  telephone: '99108-5996',
  cep: '11662-000',
  city: 'Caraguatatuba',
  cityId: 6484,
  uf: 'SP',
  street: 'Avenida Doutor Aldino Schiavi',
  neighborhood: 'Martim de Sá',
  number: '113',
}

const defaultFormState: FormState = {
  form1: false,
  form2: false,
  form3: false,
  cardForm: false,
  cardFormInvalid: false,
  showError: false,
  step1Error: false,
  step1Invalid: false,
  step2Error: false,
  disabledBtn: false
}

// >>> CONFIGS DO PROVIDER
export interface FormsContext {
  currentFields: FormFields
  formState: FormState
}

type FormUpdater = Partial<FormFields> | ((prev: FormFields) => Partial<FormFields>)

interface updateContext extends FormsContext {
  setCurrentFields: (event: ChangeEvent<HTMLInputElement>) => void
  setModelledField: (name: string, value: any) => void
  setFormState: (form: keyof FormState, value: boolean) => void
  updateFormState: (updatedValues: FormUpdater) => void
}

const defaultFormContext: updateContext = {
  currentFields: MOCK ? mockForm : defaultForm,
  formState: defaultFormState,
  setCurrentFields: (event: ChangeEvent<HTMLInputElement>) => {},
  setModelledField: (name: string, value: any) => {},
  setFormState: (form: keyof FormState, value: boolean) => {},
  updateFormState: (_updatedValues: FormUpdater) => {},
}

const FormContext = createContext<updateContext>(defaultFormContext)

export function useForm() {
  return useContext(FormContext)
}

// ===== Utils
function getCityIdByName(cities: CitiesListProps[], name?: string) {
  if (!name) return undefined
  const found = cities.find(({ cidade }) => cidade === name)
  return found?.id
}

function findStateByUF<T extends { uf?: string }>(states: T[], uf?: string) {
  if (!uf) return undefined
  return states.find(({ uf: stateUf }) => stateUf === uf)
}

// ===== Reducers
type FieldsAction =
  | { type: 'SET_FIELD_FROM_INPUT'; name: string; value: string }
  | { type: 'SET_FIELD'; name: string; value: any }
  | { type: 'MERGE'; updater: FormUpdater }

function fieldsReducer(state: FormFields, action: FieldsAction): FormFields {
  switch (action.type) {
    case 'SET_FIELD_FROM_INPUT':
      return { ...state, [action.name]: action.value }
    case 'SET_FIELD':
      return { ...state, [action.name]: action.value }
    case 'MERGE':
      if (typeof action.updater === 'function') {
        return { ...state, ...action.updater(state) }
      }
      return { ...state, ...action.updater }
    default:
      return state
  }
}

type UIFormAction = { type: 'SET_FLAG'; form: keyof FormState; value: boolean }

function formStateReducer(state: FormState, action: UIFormAction): FormState {
  switch (action.type) {
    case 'SET_FLAG':
      return { ...state, [action.form]: action.value }
    default:
      return state
  }
}

export function FormProvider({ children }: PropsWithChildren) {
  const { statesList } = useInitReq()
  const { pageTitle } = useSettings()
  const [currentState, dispatchFields] = useReducer(fieldsReducer, MOCK ? mockForm : defaultForm)
  const [currentFormState, dispatchForm] = useReducer(formStateReducer, defaultFormState)

  const setCurrentFields = useCallback(({ currentTarget: { name, value } }: ChangeEvent<HTMLInputElement>) => {
    dispatchFields({ type: 'SET_FIELD_FROM_INPUT', name, value })
  }, [])

  const setModelledField = useCallback((name: string, value: any) => {
    dispatchFields({ type: 'SET_FIELD', name, value })
  }, [])

  const setFormState = useCallback((form: keyof FormState, value: boolean) => {
    dispatchForm({ type: 'SET_FLAG', form, value })
  }, [])
  
  const updateFormState = useCallback((updatedValues: FormUpdater) => {
    dispatchFields({ type: 'MERGE', updater: updatedValues })
  }, [])

  useEffect(() => {
    if (!currentState.uf) return

    async function fetchCities(uf: string) {
      try {
        const citiesList = await requests.citiesList(uf)
        updateFormState({ citiesList })
      } catch (error) {
        console.error('Erro ao buscar cidades:', error)
      }
    }

    fetchCities(currentState.uf)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState.uf])

  useEffect(() => {
    if (currentState.citiesList.length > 0 && currentState.cityNameUpdate !== undefined) {
      const id = getCityIdByName(currentState.citiesList, currentState.cityNameUpdate)
      if (id !== undefined) setModelledField('cityId', id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState.citiesList, currentState.cityNameUpdate])

  useEffect(() => {
    const stateName = findStateByUF(statesList, currentState.uf)
    if (stateName !== undefined) setModelledField('state', stateName)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[statesList, currentState.uf])

  useEffect(() => {
    if (pageTitle === "Unixbank Telecom") setModelledField('paymentMethod', 'UNIXCARD')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[pageTitle])

  const contextValue = useMemo(() => ({
    currentFields: currentState,
    formState: currentFormState,
    setCurrentFields,
    setModelledField,
    setFormState,
    updateFormState,
  }), [currentState, currentFormState, setCurrentFields, setModelledField, setFormState, updateFormState])

  return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
}